/**
 * Plans, prices and entitlements — the single source for the API (enforcement) and the web (plan cards, upgrade sheet).
 * Browser-safe: no I/O. Plans are cumulative (each includes everything below it).
 */
/**
 * Every plan id, cheapest first. To change a plan without changing it for current subscribers, add a NEW id (no
 * underscores: Stripe price env vars are `STRIPE_PRICE_<plan>_<interval>`) and mark the old one `hidden`.
 */
export const PLAN_IDS = ['free', 'plus', 'investor', 'business'];
const FREE = ['budgets', 'goals', 'forecast', 'fileImport', 'csvExport'];
/** `amitStatement` (AMMA statements and AMIT cost-base adjustments) sits with the CGT schedule: without it ETF cost bases are wrong. */
const PLUS = [...FREE, 'bankFeeds', 'tradeInbox', 'allReports', 'cgtSchedule', 'amitStatement', 'cardRewards'];
const INVESTOR = [...PLUS, 'smsfTrustCompany', 'parcelOptimiser', 'whatIf', 'corporateActions', 'benchmarking', 'accountantPack', 'xlsxPdfExport'];
const BUSINESS = [...INVESTOR, 'invoicing', 'vatGstFiling', 'memberRoles', 'prioritySupport'];
export const PLANS = {
    free: {
        id: 'free', name: 'Free', blurb: 'Budget, goals and forecast for one set of finances.', features: FREE, limits: { entities: 1, holdings: 10 },
        highlights: ['1 entity', 'Budgets, goals & forecast', 'Statement upload (CSV, OFX, QIF, PDF)', 'Everyday reports', 'Up to 10 holdings', 'CSV export'],
    },
    plus: {
        id: 'plus', name: 'Plus', blurb: 'Everything for one household, connected.', features: PLUS, limits: { entities: 1, holdings: null },
        highlights: ['Unlimited accounts & holdings', 'Bank feeds', 'Trades emailed in from your broker', 'All reports', 'CGT schedule', 'AMIT cost-base adjustments (AMMA statements)', 'Card rewards calculator'],
    },
    investor: {
        id: 'investor', name: 'Investor', blurb: 'Several entities, SMSF and tax-smart selling.', features: INVESTOR, limits: { entities: 5, holdings: null },
        highlights: ['Up to 5 entities incl. SMSF, trust, company', 'Parcel optimiser & what-if', 'Other corporate actions (splits, takeovers, demergers)', 'Benchmarking', 'Accountant pack', 'XLSX & PDF exports'],
    },
    business: {
        id: 'business', name: 'Business', blurb: 'Run the books for the businesses you own.', features: BUSINESS, limits: { entities: 15, holdings: null },
        highlights: ['Up to 15 entities', 'Invoicing', 'VAT / GST returns', 'Accountant access (member roles)', 'Priority support'],
    },
};
/** Whole currency units (not cents) — display prices; Stripe holds the real price objects. */
export const PRICES = {
    AUD: { plus: { monthly: 9, annual: 89 }, investor: { monthly: 19, annual: 179 }, business: { monthly: 39, annual: 389 } },
    USD: { plus: { monthly: 6, annual: 59 }, investor: { monthly: 13, annual: 129 }, business: { monthly: 27, annual: 269 } },
    GBP: { plus: { monthly: 5, annual: 49 }, investor: { monthly: 11, annual: 109 }, business: { monthly: 23, annual: 229 } },
};
export const TRIAL_PLAN = 'investor';
export const TRIAL_DAYS = 30;
/** Founding members' Investor annual price, whole currency units, locked while the subscription stays active (its own Stripe price, so list-price rises don't touch it). */
export const FOUNDING_PRICES = { AUD: 99, USD: 69, GBP: 59 };
export const FOUNDING_LIMIT = 100;
export const planCurrencyFor = (currency) => (currency === 'USD' || currency === 'GBP' ? currency : 'AUD');
export const isPlanId = (s) => typeof s === 'string' && PLAN_IDS.includes(s);
export const planRank = (p) => PLAN_IDS.indexOf(p);
export const planAllows = (plan, feature) => PLANS[plan].features.includes(feature);
/** Plans on sale, cheapest first (legacy `hidden` plans left out). */
export const OFFERED_PLAN_IDS = PLAN_IDS.filter((p) => !PLANS[p].hidden);
/** The cheapest offered plan with the feature. */
export const requiredPlanFor = (feature) => OFFERED_PLAN_IDS.find((p) => planAllows(p, feature)) ?? 'business';
/** The cheapest offered plan allowing `n` owned entities. */
export const planForEntities = (n) => OFFERED_PLAN_IDS.find((p) => PLANS[p].limits.entities >= n) ?? null;
/** Paid plan while the subscription is active/past_due, else the trial plan while the trial runs, else Free. */
export const effectivePlan = (u, nowMs = Date.now()) => {
    const paid = isPlanId(u.plan) && u.plan !== 'free' && (u.planStatus === 'active' || u.planStatus === 'past_due' || u.planStatus === 'trialing');
    if (paid)
        return u.plan;
    if (u.trialEndsAt && Date.parse(u.trialEndsAt) > nowMs)
        return TRIAL_PLAN;
    return 'free';
};
/** Whole days left in the trial (rounded up), 0 when over or none. */
export const trialDaysLeft = (u, nowMs = Date.now()) => {
    if (!u.trialEndsAt)
        return 0;
    return Math.max(0, Math.ceil((Date.parse(u.trialEndsAt) - nowMs) / 86_400_000));
};
export const FEATURE_LABELS = {
    budgets: 'Budgets', goals: 'Goals', forecast: 'Forecast', fileImport: 'File import', csvExport: 'CSV export',
    bankFeeds: 'Bank feeds', tradeInbox: 'Trade inbox', allReports: 'All reports', cgtSchedule: 'CGT schedule', amitStatement: 'AMIT cost-base adjustments', cardRewards: 'Card rewards calculator',
    smsfTrustCompany: 'SMSF, trust & company entities', parcelOptimiser: 'Parcel optimiser', whatIf: 'What-if selling',
    corporateActions: 'Corporate actions', benchmarking: 'Benchmarking', accountantPack: 'Accountant pack', xlsxPdfExport: 'XLSX & PDF exports',
    invoicing: 'Invoicing', vatGstFiling: 'VAT / GST returns', memberRoles: 'Accountant access & member roles', prioritySupport: 'Priority support',
};
/** What each feature does, in a sentence: shown wherever a locked feature is listed, so it stays discoverable. */
export const FEATURE_DETAILS = {
    budgets: 'Plan spending with jars or a detailed budget.',
    goals: 'Save towards goals and see whether you’re on track.',
    forecast: 'Project your balances and net worth forward.',
    fileImport: 'Upload statements from banks and brokers (CSV, OFX, QIF, PDF).',
    csvExport: 'Download your data as CSV.',
    bankFeeds: 'Transactions arrive from your bank automatically, read-only.',
    tradeInbox: 'Your broker emails each trade confirmation to your own Mintage address and the trade is ready to confirm.',
    allReports: 'Tax, business, trend and audit reports on top of the everyday money reports.',
    cgtSchedule: 'Capital gains and losses for the year, laid out to the tax return labels, plus realised gains.',
    amitStatement: 'Enter AMMA/AMIT statements so ETF and trust cost bases stay right.',
    cardRewards: 'See which card earns the most on your real spending.',
    smsfTrustCompany: 'Track an SMSF, trust or company alongside your household.',
    parcelOptimiser: 'Choose which parcels to sell for the lowest tax, and find losses to harvest before 30 June.',
    whatIf: 'See the tax on a sale before you place it.',
    corporateActions: 'Splits, takeovers, demergers and other share events, applied with a preview.',
    benchmarking: 'Compare your returns with an index or ETF using your own cashflows.',
    accountantPack: 'One download with everything your accountant asks for.',
    xlsxPdfExport: 'Export reports as Excel or PDF.',
    invoicing: 'Send invoices and bills and track what’s owed.',
    vatGstFiling: 'Prepare and lodge VAT / GST returns.',
    memberRoles: 'Invite your partner or accountant with their own access.',
    prioritySupport: 'Faster answers from the team.',
};
/** Reports that need more than Free; every other report key is on every plan. */
export const REPORT_FEATURES = {
    'profit-loss': 'allReports', 'taxes-paid': 'allReports', 'tax-deductible': 'allReports', 'gst-bas': 'allReports',
    vat: 'allReports', 'ec-sales-list': 'allReports', 'audit-log': 'allReports',
    cgt: 'cgtSchedule', gains: 'cgtSchedule', performance: 'benchmarking',
};
/** Parcel strategies anyone can use; the rest pick parcels to optimise tax (`parcelOptimiser`). */
export const BASIC_PARCEL_STRATEGIES = ['fifo', 'manual'];
//# sourceMappingURL=plans.js.map